import { Box, HStack, Icon, Text } from '@chakra-ui/react';
import { MentionBoostDeliveryState, MentionBoostPlacementMode } from '@fluenceur/types';
import type { MentionBoostDto } from '@fluenceur/types';
import { ShieldCheck } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { BoostAppliedPlacement } from './BoostAppliedPlacement';
import { BoostAppliedUtm } from './BoostAppliedUtm';
import { BoostDeliveryStatus } from './BoostDeliveryStatus';

interface MentionBoostLaunchedViewProps {
  boost: MentionBoostDto;
}

/**
 * Read-only surface for a launched boost. A launched boost is not re-edited:
 * the setup form gives way to the honest delivery state. Thin wrapper so the
 * setup panel can branch cleanly between the editable form and this view.
 */
export function MentionBoostLaunchedView({ boost }: MentionBoostLaunchedViewProps) {
  // Legacy snapshots predate the field; treat a missing value as no tracking.
  const appliedUtm = boost.config.utmParameters ?? [];

  return (
    <Box px={{ base: 4, md: 6 }} pt={1} pb={6}>
      <BoostDeliveryStatus
        deliveryState={boost.deliveryState}
        lastErrorCode={boost.lastErrorCode}
      />
      <RollbackNote boost={boost} />
      <BoostAppliedPlacement config={boost.config} />
      <BoostAppliedUtm parameters={appliedUtm} targetUrl={boost.config.targetUrl} />
    </Box>
  );
}

/**
 * After a failed launch inside a brand campaign, the first question is "what
 * did you leave in my account?". The answer — nothing — belongs right under the
 * failure reason, naming the campaign that stayed untouched.
 */
function RollbackNote({ boost }: { boost: MentionBoostDto }) {
  const { t } = useTranslation();

  const isFailedInBrandCampaign =
    boost.deliveryState === MentionBoostDeliveryState.FAILED &&
    boost.config.placementMode === MentionBoostPlacementMode.EXISTING_CAMPAIGN &&
    !!boost.config.parentCampaignName;

  if (!isFailedInBrandCampaign) return null;

  return (
    <HStack gap={1.5} align="flex-start" mt={3}>
      <Icon size="xs" color="fg.muted" mt={0.5}>
        <ShieldCheck />
      </Icon>
      <Text fontSize="xs" color="fg.muted">
        {t('mentions.boost.detail.rollbackNote', {
          campaign: boost.config.parentCampaignName,
        })}
      </Text>
    </HStack>
  );
}
